<template>
  <!-- Modal Structure -->
  <div v-if="show" class="certificate-modal">
    <div class="modal-overlay" @click="close"></div>
    
    <div class="modal-content bg-white rounded-3 shadow-lg">
      <div class="modal-body p-4">
        <div ref="certificateCanvas" class="certificate-template elegant-theme">
          <div class="certificate-border">
            <div class="certificate-header text-center mb-3">
              <img src="@/assets/AppLogoFull.png" alt="Logo del Curso" class="elegant-logo mb-2 mt-4">
              <h1 class="certificate-title">Certificado de Finalización</h1>
              <p class="subheading">Este reconocimiento es otorgado a</p>
            </div>

            <div class="certificate-body text-center py-3">
              <h3 class="student-name fw-bold">{{ fullName }}</h3>
              <p class="completion-text">Por haber finalizado satisfactoriamente el curso</p>
              <h3 class="course-title text-primary">{{ courseTitle }}</h3>
            </div>

            <div class="certificate-footer-area d-flex justify-content-around mt-5">
              <div class="signature-block">
                <div class="signature-line mx-auto"></div>
                <p class="signature-name">{{ instructorName }}</p>
                <p class="signature-role">Instructor</p>
              </div>
              <div class="signature-block">
                <div class="signature-line mx-auto"></div>
                <p class="signature-name">{{ completionDate }}</p>
                <p class="signature-role">Fecha de finalización</p>
              </div>
            </div>

          </div>
          <p class="certificate-id bottom-0 start-0 end-0 text-center text-muted small">ID del Certificado: {{ certificateId }}</p>
        </div>
      </div>
      
      <!-- Floating Download Button -->
      <button @click="downloadCertificate" class="btn btn-primary position-absolute end-4 bottom-7 m-2 rounded-5 " style="width: 150px; height: 40px">
        <i class="fas fa-download pe-2"> </i>Descargar
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import html2canvas from 'html2canvas';
import 'bootstrap/dist/css/bootstrap.min.css';
import { CoursesClient } from "@/services/CoursesClient";

const coursesClient = new CoursesClient();

const props = defineProps({
show: {
  type: Boolean,
  default: false
},

});

const emit = defineEmits(['close']);
const store = useStore();
const route = useRoute();

// Certificate data
const courseTitle = ref("");
const instructorName = ref("");
const completionDate = ref(new Date().toLocaleDateString());
const certificateId = ref("CERT-" + Math.random().toString(36).substr(2, 9).toUpperCase());
const certificateCanvas = ref(null);

// Get user data from store
const userData = computed(() => store.state.userData);
const fullName = computed(() => {
return `${userData.value?.firstName || ''} ${userData.value?.lastName || ''}`.trim();
});

const close = () => {
emit('close');
};

const generateCertificateId = () => {
return "CERT-" + Math.random().toString(36).substr(2, 9).toUpperCase();
};

const fetchCourseData = async () => {
try {
  const courseId = route.params.curso;

  const response = await coursesClient.getCourseById(courseId);
  console.log("Course data:", response);
  courseTitle.value = response.titulo || "Curso sin título";
  instructorName.value = response.profesor_nombre || "Instructor";
} catch (error) {
  console.error("Error fetching course data:", error);
  courseTitle.value = "Curso no disponible";
  instructorName.value = "Instructor no disponible";
}
};

onMounted(() => {
certificateId.value = generateCertificateId();
fetchCourseData();
});

const downloadCertificate = async () => {
try {
  const canvas = await html2canvas(certificateCanvas.value, {
    scale: 2,
    logging: false,
    useCORS: true,
    backgroundColor: null
  });
  
  const link = document.createElement('a');
  link.download = `${fullName.value}_${courseTitle.value.replace(/\s+/g, '_')}_Certificado.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
} catch (error) {
  console.error('Error al descargar el certificado:', error);
}
};
</script>
  
  <style scoped>
  .certificate-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
  }
  
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }
  
  .modal-content {
    position: relative;
    max-width: 1000px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .elegant-theme {
    font-family: 'Lato', sans-serif;
    background: #fffdf8;
    border: 12px double #d4af37;
    width: 100%;
    height: 100%;
    position: relative;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
  
  .elegant-logo {
    height: 120px;
  }
  
  .certificate-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.7rem;
    color: #2c3e50;
    margin-top: 1rem;
    font-weight: 700;
  }
  
  .subheading {
    font-size: 1.1rem;
    color: #555;
  }
  
  .student-name {
    font-size: 1.8rem;
    color: #2c3e50;
    margin: 1rem 0;
    text-transform: uppercase;
  }
  
  .course-title {
    font-size: 1.8rem;
    margin: 1rem 0;
  }
  
  .signature-line {
    width: 220px;
    height: 1px;
    background-color: #333;
    margin: 0 auto 0.5rem;
  }
  
  .signature-name {
    font-size: 1.2rem;
    margin-bottom: 0.2rem;
  }
  
  .signature-role {
    font-size: 0.9rem;
    color: #555;
  }
  
  @media (max-width: 768px) {
    .certificate-title {
      font-size: 2rem;
    }
    
    .student-name, .course-title {
      font-size: 1.5rem;
    }
    
    .signature-line {
      width: 150px;
    }
    
    .elegant-logo {
      height: 70px;
    }
  }
  </style>